import { useState, type ReactNode } from 'react';
import { Input } from './Input.js';
import { Button } from './Button.js';
import { IconButton } from './IconButton.js';
import { Icon } from '../Icon.js';
import { useToast } from './Toast.js';

export interface KVRowProps {
  label: string;
  value?: ReactNode;
  renderValue?: (v: string) => ReactNode;
  onSave?: (newValue: string) => Promise<void>;
  type?: 'text' | 'email' | 'url' | 'tel' | 'number';
  placeholder?: string;
  validate?: (v: string) => string | null;
  last?: boolean;
}

export function KVRow({
  label,
  value,
  renderValue,
  onSave,
  type = 'text',
  placeholder,
  validate,
  last = false,
}: KVRowProps) {
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hovered, setHovered] = useState(false);

  const borderBottom = last ? undefined : '1px solid var(--evo-stroke)';

  function startEdit() {
    setDraft(typeof value === 'string' ? value : '');
    setError(null);
    setEditing(true);
  }

  function cancelEdit() {
    setEditing(false);
    setError(null);
  }

  async function handleSave() {
    if (validate) {
      const msg = validate(draft);
      if (msg !== null) {
        setError(msg);
        return;
      }
    }
    setError(null);
    setSaving(true);
    try {
      await onSave!(draft);
      setEditing(false);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Error al guardar';
      setError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  }

  if (editing) {
    return (
      <div style={{ padding: 'var(--evo-detail-kv-row-py) 0', borderBottom }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--evo-space-2)' }}>
          <span style={{
            width: 'var(--evo-detail-kv-label-w)',
            flexShrink: 0,
            paddingTop: 8,
            fontSize: 'var(--evo-fs-label)',
            color: 'var(--evo-text-muted)',
            fontFamily: 'var(--evo-font)',
          }}>
            {label}
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Input
              value={draft}
              onChange={setDraft}
              type={type}
              size="sm"
              fullWidth
              autoFocus
              disabled={saving}
              {...(error !== null ? { error } : {})}
              onKeyDown={e => {
                if (e.key === 'Enter') { void handleSave(); }
                if (e.key === 'Escape') { cancelEdit(); }
              }}
            />
          </div>
          <div style={{ display: 'flex', gap: 'var(--evo-space-1)', paddingTop: 1 }}>
            <Button
              variant="ghost"
              tone="cyan"
              size="sm"
              loading={saving}
              disabled={saving}
              onClick={() => { void handleSave(); }}
            >
              Guardar
            </Button>
            <Button
              variant="ghost"
              tone="muted"
              size="sm"
              disabled={saving}
              onClick={cancelEdit}
            >
              Cancelar
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Display mode
  function getDisplayValue(): ReactNode {
    if (value === null || value === undefined || value === '') {
      return (
        <span style={{ color: 'var(--evo-text-muted)', fontStyle: 'italic' }}>
          {placeholder ?? '—'}
        </span>
      );
    }
    if (typeof value === 'string' && renderValue) {
      return renderValue(value);
    }
    return value as ReactNode;
  }

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--evo-space-2)',
        padding: 'var(--evo-detail-kv-row-py) 0',
        borderBottom,
        minHeight: 'var(--evo-detail-kv-row-min-h)',
        height: '100%',
        boxSizing: 'border-box',
      }}
      onMouseEnter={onSave !== undefined ? () => setHovered(true) : undefined}
      onMouseLeave={onSave !== undefined ? () => setHovered(false) : undefined}
    >
      <span style={{
        width: 'var(--evo-detail-kv-label-w)',
        flexShrink: 0,
        fontSize: 'var(--evo-fs-label)',
        color: 'var(--evo-text-muted)',
        fontFamily: 'var(--evo-font)',
      }}>
        {label}
      </span>
      <span style={{
        flex: 1,
        minWidth: 0,
        fontSize: 'var(--evo-list-title-fs)',
        color: 'var(--evo-text-strong)',
        fontFamily: 'var(--evo-font)',
        wordBreak: 'break-word',
      }}>
        {getDisplayValue()}
      </span>
      {onSave !== undefined && (
        <div style={{ opacity: hovered ? 1 : 0, transition: 'opacity 120ms linear', flexShrink: 0 }}>
          <IconButton
            icon={<Icon name="pencil" size={13} />}
            ariaLabel={`Editar ${label}`}
            variant="ghost"
            tone="muted"
            size="sm"
            onClick={startEdit}
          />
        </div>
      )}
    </div>
  );
}
